import CustomPagination from '@/platform/common/components/CustomPagination/CustomPagination';
import EmployeeCard from '@/platform/common/components/EmployeeCard/EmployeeCard';
import NoDataRow from '@/platform/common/components/NoDataRow/NoDataRow';
import PlaceholderValue from '@/platform/common/components/PlaceholderValue/PlaceholderValue';
import SearchBarFilter from '@/platform/common/components/SearchBarFilter/SearchBarFilter';
import { useFilters } from '@/platform/hooks/useFilters';
import { localDate } from '@/platform/lib/utils';
import {
    Badge,
    Container,
    FilterBarDropdown,
    Segment,
    SegmentContent,
    SegmentFooter,
    SegmentHeader,
    Table,
    TableBody,
    TableCell,
    TableHeader,
    TableHeaderCell,
    TableRow,
} from '@/platform/ui';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Link, usePage } from '@inertiajs/react';
import dayjs from 'dayjs';
import EmploymentsPopup from './components/EmploymentsPopup';
import Navigation from './components/Navigation';

const CustomerPendingDeletionView = () => {
    const { auth, entitlements, categories, states } = usePage().props;
    const { filters, updateFilters, isSorted } = useFilters(['entitlements']);

    return (
        <Container fluid>
            <Navigation />

            <Segment>
                <SegmentHeader
                    header={`Binnenkort te verwijderen LKV-aanspraken (${entitlements?.meta?.total || 0})`}
                    subheader={`Deze LKV-aanspraken worden op ${dayjs().endOf('month').format('D MMMM YYYY')} verwijderd omdat de bewaartermijn is verstreken. Controleer ze voordat ze definitief weg zijn.`}
                />
                <SegmentContent>
                    <SearchBarFilter only={['entitlements']}>
                        <FilterBarDropdown
                            multiple
                            selectAll
                            label={`Entiteiten (${filters?.entity_ids?.length || 0}/${auth?.customer?.entities?.length || 0})`}
                            options={auth.customer?.entities?.map(entity => ({
                                id: entity.id,
                                name: entity.name,
                            }))}
                            value={filters?.entity_ids || []}
                            onChange={value =>
                                updateFilters('entity_ids', value)
                            }
                        />
                        <FilterBarDropdown
                            clearable
                            label={`Categorie (${categories?.find(cat => cat.id == filters?.category)?.name || 'Alle'})`}
                            options={categories || []}
                            value={filters?.category || ''}
                            onChange={value => updateFilters('category', value)}
                        />
                        <FilterBarDropdown
                            clearable
                            label={`Status (${states?.find(stat => stat.id == filters?.state)?.name || 'Alle'})`}
                            options={states || []}
                            value={filters?.state || ''}
                            onChange={value => updateFilters('state', value)}
                        />
                    </SearchBarFilter>

                    <Table striped>
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Medewerker</TableHeaderCell>
                                {filters?.entity_ids?.length > 1 && (
                                    <TableHeaderCell>Entiteit</TableHeaderCell>
                                )}
                                <TableHeaderCell
                                    sorted={isSorted('start_date')}
                                    onClick={() =>
                                        updateFilters('sort', 'start_date')
                                    }
                                >
                                    Datum indienst
                                </TableHeaderCell>
                                <TableHeaderCell>
                                    Datum uitdienst
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    Categorie
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='center'>
                                    State
                                </TableHeaderCell>
                                <TableHeaderCell textAlign='right'>
                                    Document
                                </TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {entitlements?.meta?.total === 0 && (
                                <NoDataRow
                                    numberOfColumns={
                                        filters?.entity_ids?.length > 1 ? 8 : 7
                                    }
                                />
                            )}

                            {entitlements?.data?.map(entitlement => (
                                <TableRow key={entitlement.id}>
                                    <TableCell>
                                        <EmployeeCard
                                            employee={entitlement?.employee}
                                        />
                                    </TableCell>
                                    {filters?.entity_ids?.length > 1 && (
                                        <TableCell>
                                            {
                                                entitlement?.employee?.entity
                                                    ?.name
                                            }
                                        </TableCell>
                                    )}
                                    <TableCell>
                                        <EmploymentsPopup
                                            entitlement={entitlement}
                                        />
                                    </TableCell>
                                    <TableCell>
                                        <PlaceholderValue
                                            isPresent={
                                                entitlement.employment_end_date
                                            }
                                            placeholder='Onbepaald'
                                        >
                                            {localDate(
                                                entitlement.employment_end_date
                                            )}
                                        </PlaceholderValue>
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        <Badge
                                            color={entitlement.category_color}
                                        >
                                            {entitlement.category_label}
                                        </Badge>
                                    </TableCell>
                                    <TableCell textAlign='center'>
                                        <Badge color={entitlement.state_color}>
                                            {entitlement.state_label}
                                        </Badge>
                                    </TableCell>
                                    <TableCell textAlign='right'>
                                        {(entitlement?.dgv_document_id ||
                                            entitlement?.dgr_identity_check
                                                ?.document_id) && (
                                            <Link
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                href={`/customers/${auth?.user?.selected_customer_id}/services/lkv/${entitlement.id}/download`}
                                                className='inline-flex'
                                            >
                                                <ArrowDownTrayIcon className='size-5' />
                                            </Link>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </SegmentContent>

                <SegmentFooter>
                    <CustomPagination
                        meta={entitlements?.meta}
                        only={['entitlements']}
                    />
                </SegmentFooter>
            </Segment>
        </Container>
    );
};

export default CustomerPendingDeletionView;
